import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { gebaeudeSearchModeName } from '../gebaede.constants';
import { AsyncPipe, Location, NgTemplateOutlet } from '@angular/common';
import { ErweiterungConfigResponse } from '../../../core/base-api';
import { ComponentLocationStrategy } from '../../../shared/services/location-strategy/component-location-strategy';
import { GebaeudeSuchergebnisLocationStrategy } from '../../../shared/services/location-strategy/gebaeude-suchergebnis-location-strategy';
import { KompaktGebaeudePipe } from '../../../shared/kompaktdarstellung/gebaeude/kompakt-gebaeude-pipe';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { FunctionbarSearchresultButtonsComponent } from '../../../shared/components/title-functionbar/functionbar-searchresult-buttons/functionbar-searchresult-buttons.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { LinkComponent, NavTarget } from '../../../shared/components/link/link.component';
import { GebaeudeListGrundstueckComponent } from './grundstueck/gebaeude-list-grundstueck.component';
import { KompaktGebaeudeeingangsadressePipe } from '../../../shared/kompaktdarstellung/gebaeudeeingangsadresse/kompakt-gebaeudeeingangsadresse-pipe';
import { AbstractComponent } from '../../../shared/components/abstract-component/abstract.component';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { Observable, Subscription } from 'rxjs';
import { AvgebaeudeService, GebaeudeSearchResponse } from '../../../core/av-api';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import {
  GbgrundstueckService,
  GebaeudeEingangAdresseListEntry,
  GebaeudeListEntry,
  GrundstueckId,
  GrundstueckListEntry
} from '../../../core/gb-api';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { FormatConfigService } from '../../../core/services/format-config.service';
import { SearchStateService } from '../../../shared/services/search-state.service';
import { SearchMode } from '../../../shared/enum/searchMode';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { HistoryMode } from '../../../core/services/HistoryMode';
import { GwrLinkComponent } from '../../../shared/components/gwr-link/gwr-link.component';
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;

@Component({
  selector: 'cap-gebaeude-list',
  templateUrl: './gebaeude-list.component.html',
  styleUrls: ['./gebaeude-list.component.scss'],
  providers: [
    {
      provide: ComponentLocationStrategy,
      useClass: GebaeudeSuchergebnisLocationStrategy
    },
    KompaktGebaeudePipe
  ],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    FunctionbarSearchresultButtonsComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    ColumnHeaderComponent,
    LinkComponent,
    GebaeudeListGrundstueckComponent,
    TranslatePipe,
    AsyncPipe,
    KompaktGebaeudeeingangsadressePipe,
    GwrLinkComponent
  ]
})
export class GebaeudeListComponent extends AbstractComponent implements OnDestroy, OnInit {
  tableSortModel = new TableSortModel<GebaeudeListEntryVm>('table', this.comparatorService);

  gebaeudeListLoader: ListLoader<GebaeudeListEntryVm>;
  pageSize = 15;
  selected = [];
  searchResponse;

  navTargets = NavTarget;

  gebaeudeSubscription: Subscription;
  avPlusErweiterung;

  constructor(
    private avgebaeudeService: AvgebaeudeService,
    protected router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private capAlertService: CapAlertService,
    private location: Location,
    private kompaktGebaeudePipe: KompaktGebaeudePipe,
    private grundstueckService: GbgrundstueckService,
    private comparatorService: ComparatorService,
    private appFormatConfigService: FormatConfigService,
    private stateService: SearchStateService
  ) {
    super();
    keysToTranslate<string>([
      'VersicherungsNummerAbkürzung',
      'KantGid',
      'GebäudeeingangsadresseAdressen',
      'Gebaeude_Einzahl',
      'Status',
      'GrundstückGrundstücke',
      'EinzelobjektAbkürzung',
      'AnzeigeGebäude'
    ]);
    this.tableSortModel
      .addColumn('egid', (a, b) => this.comparatorService.orderByComparator(a.model.egid, b.model.egid))
      .switchSortDirection();
    this.tableSortModel.addColumn('kantGid', (a, b) => this.comparatorService.orderByComparator(a.model.kantGid, b.model.kantGid));
    this.tableSortModel.addColumn('versicherungsNummer', (a, b) =>
      this.comparatorService.orderByComparator(a.model.versicherungsNummer, b.model.versicherungsNummer)
    );
    this.tableSortModel.addColumn('kompaktGebaeudeArt', (a, b) =>
      this.comparatorService.orderByComparator(a.kompaktGebaeudeArt, b.kompaktGebaeudeArt)
    );
    this.tableSortModel.addColumn('kompaktEingangsAdresse', (a, b) =>
      this.comparatorService.eingangadressenListEntryListComparator(a.sortedEingangsAdressen, b.sortedEingangsAdressen)
    );
    this.tableSortModel.addColumn('gebaeudeStatus', (a, b) =>
      this.comparatorService.orderByComparator(a.model.gebaeudeStatus, b.model.gebaeudeStatus)
    );
    this.tableSortModel.addColumn('istEinzelobjekt', (a, b) =>
      this.comparatorService.orderByComparator(a.model.istEinzelobjekt, b.model.istEinzelobjekt)
    );
  }

  ngOnInit(): void {
    this.addSub(
      this.appFormatConfigService.erweiterungsConfig$.subscribe((config) => {
        this.avPlusErweiterung = config?.activeErweiterungen.includes(ActiveErweiterungenEnum.AV_PLUS);
      })
    );
    this.route.params.subscribe((params) => {
      const searchMode: SearchMode = params[gebaeudeSearchModeName];
      this.route.queryParams.subscribe((param) => {
        const requestParam = param['request'];
        this.stateService.lastSearchMode = searchMode;
        if (requestParam) {
          const request = JSON.parse(requestParam);
          this.loadGebaeudeStrassen(request, searchMode);
        }
      });
    });
  }

  private loadGebaeudeStrassen(request: any, searchMode: SearchMode) {
    this.gebaeudeSubscription = this.getGebaeudeListSubscription(request)[searchMode].subscribe({
      next: (response) => {
        this.fillGebaeudeListFrom(response);
      },
      error: (err) => {
        this.handleError(err);
      }
    });
  }

  private handleError(err) {
    throw err;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.gebaeudeSubscription?.unsubscribe();
  }

  navigateBackToSearch() {
    this.location.back();
  }

  private fillGebaeudeListFrom(response: GebaeudeSearchResponse) {
    this.searchResponse = response;
    if (response.results.length === 0) {
      this.capAlertService.info(this.translate.instant('KeineResultateGefunden'), undefined, true);
      this.navigateBackToSearch();
    }

    const gebaeudeList = [];
    for (const geb of response.results) {
      gebaeudeList.push(new GebaeudeListEntryVm(geb, this.kompaktGebaeudePipe, this.comparatorService, this.grundstueckService));
    }

    this.gebaeudeListLoader = new ListLoaderPreloaded<GebaeudeListEntryVm>(this.pageSize, 1, gebaeudeList);
    this.gebaeudeListLoader.applySortModel(this.tableSortModel);
    this.addLoader(this.gebaeudeListLoader);
  }

  private getGebaeudeListSubscription(request) {
    return {
      searchStandard: this.avgebaeudeService.searchGebaeudeStandard(request),
      searchFachId: this.avgebaeudeService.searchGebaeudeEgid(request)
    };
  }

  getQueryParams(listEntry: GebaeudeListEntryVm) {
    if (!listEntry || !listEntry.model?.masterId) {
      return;
    }
    return { id: listEntry.model?.masterId.id, versionId: listEntry.model?.id };
  }
}

export type GRUNDSTUECKE_VORHANDEN_ENUM = null | 'GRUNDSTUECKE_VORHANDEN' | 'GRUNDSTUECKE_LADEN';

export class GebaeudeListEntryVm {
  kompaktGebaeudeArt: string;
  grundstuecke$;
  sortedEingangsAdressen: GebaeudeEingangAdresseListEntry[];

  constructor(
    public model: GebaeudeListEntry,
    private kompaktGebaeudePipe: KompaktGebaeudePipe,
    private comparatorService: ComparatorService,
    private grundstueckService: GbgrundstueckService
  ) {
    this.kompaktGebaeudeArt = this.kompaktGebaeudePipe.transform(model);
    this.grundstuecke$ = this.getGrundstuecke(model.grundstuecke);
    this.sortedEingangsAdressen = this.sortAndFilterEingangsAdresse();
  }

  sortAndFilterEingangsAdresse(): GebaeudeEingangAdresseListEntry[] {
    const adressen = this.model.eingangAdresse.filter(
      (item, index, self) => !self.filter((v, i) => JSON.stringify(item) === JSON.stringify(v) && i < index).length
    );
    return adressen.sort((a, b) => this.comparatorService.eingangadressenListEntryComparator(a, b));
  }

  private getGrundstuecke(grundstuecke: Array<GrundstueckId>): Observable<GrundstueckListEntry[]> {
    if (grundstuecke && grundstuecke.length > 0) {
      return this.grundstueckService.loadGrundstueckListEntriesByIds(
        HistoryMode.HistoryMode.OHNE_HIST,
        grundstuecke.map((a) => a.id.toString())
      );
    }
    return Observable.create(null);
  }
}
